// ================================================
// REPORTS HOOKS - Real Data
// ================================================
// React Query hooks for fetching real report data from database

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { startOfMonth, endOfMonth, format } from 'date-fns';

// ================================================
// DASHBOARD SUMMARY (for main reports page)
// ================================================

export interface DashboardSummary {
  salesThisMonth: number;
  salesGrowth: number;
  expensesThisMonth: number;
  expensesGrowth: number;
  netCashFlow: number;
  cashFlowStatus: 'positive' | 'negative';
  tvaToPay: number;
}

export function useReportsDashboardSummary(companyId: string) {
  return useQuery({
    queryKey: ['reports-dashboard-summary', companyId],
    queryFn: async (): Promise<DashboardSummary> => {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      
      const lastMonthStart = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const lastMonthEnd = endOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));

      // Get this month's sales
      const { data: thisMonthInvoices } = await supabase
        .from('invoices')
        .select('total_ttc')
        .eq('company_id', companyId)
        .eq('status', 'paid')
        .gte('invoice_date', monthStart.toISOString())
        .lte('invoice_date', monthEnd.toISOString());

      const salesThisMonth = thisMonthInvoices?.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0) || 0;

      // Get last month's sales for growth calculation
      const { data: lastMonthInvoices } = await supabase
        .from('invoices')
        .select('total_ttc')
        .eq('company_id', companyId)
        .eq('status', 'paid')
        .gte('invoice_date', lastMonthStart.toISOString())
        .lte('invoice_date', lastMonthEnd.toISOString());

      const salesLastMonth = lastMonthInvoices?.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0) || 0;
      const salesGrowth = salesLastMonth > 0 
        ? ((salesThisMonth - salesLastMonth) / salesLastMonth) * 100 
        : 0;

      // Get this month's expenses
      const { data: thisMonthExpenses } = await supabase
        .from('treasury_transactions')
        .select('amount')
        .eq('company_id', companyId)
        .eq('type', 'expense')
        .gte('transaction_date', monthStart.toISOString())
        .lte('transaction_date', monthEnd.toISOString());

      const expensesThisMonth = thisMonthExpenses?.reduce((sum, exp) => sum + Math.abs(exp.amount || 0), 0) || 0;

      // Get last month's expenses for growth calculation
      const { data: lastMonthExpenses } = await supabase
        .from('treasury_transactions')
        .select('amount')
        .eq('company_id', companyId)
        .eq('type', 'expense')
        .gte('transaction_date', lastMonthStart.toISOString())
        .lte('transaction_date', lastMonthEnd.toISOString());

      const expensesLastMonth = lastMonthExpenses?.reduce((sum, exp) => sum + Math.abs(exp.amount || 0), 0) || 0;
      const expensesGrowth = expensesLastMonth > 0 
        ? ((expensesThisMonth - expensesLastMonth) / expensesLastMonth) * 100 
        : 0;

      // Calculate net cash flow
      const netCashFlow = salesThisMonth - expensesThisMonth;

      // Calculate TVA to pay (TVA collectée - TVA déductible)
      const tvaCollectee = thisMonthInvoices?.reduce((sum, inv) => sum + (inv.total_tva || 0), 0) || 0;
      
      // Assuming purchases/expenses with TVA are stored somewhere
      // For now, estimate as 19% of expenses (if applicable)
      const tvaDeductible = 0; // TODO: Calculate from purchase invoices
      const tvaToPay = tvaCollectee - tvaDeductible;

      return {
        salesThisMonth,
        salesGrowth,
        expensesThisMonth,
        expensesGrowth: -expensesGrowth, // Negative because higher expenses = bad
        netCashFlow,
        cashFlowStatus: netCashFlow >= 0 ? 'positive' : 'negative',
        tvaToPay,
      };
    },
    enabled: !!companyId,
  });
}

// ================================================
// SALES REPORT DATA
// ================================================

export interface SalesReportData {
  totalSales: number;
  totalInvoices: number;
  averageInvoiceValue: number;
  salesByClient: { client_name: string; total: number }[];
  salesByProduct: { product_name: string; quantity: number; total: number }[];
  salesOverTime: { date: string; amount: number }[];
  topProducts: { name: string; quantity: number; revenue: number }[];
  topClients: { name: string; invoices: number; revenue: number }[];
}

export function useSalesReportData(
  companyId: string,
  startDate: Date,
  endDate: Date
) {
  return useQuery({
    queryKey: ['sales-report', companyId, startDate, endDate],
    queryFn: async (): Promise<SalesReportData> => {
      // Get all invoices in date range
      const { data: invoices } = await supabase
        .from('invoices')
        .select(`
          *,
          client:clients(name),
          invoice_items(
            quantity,
            total_ht,
            product:products(name)
          )
        `)
        .eq('company_id', companyId)
        .gte('invoice_date', startDate.toISOString())
        .lte('invoice_date', endDate.toISOString());

      if (!invoices) {
        return {
          totalSales: 0,
          totalInvoices: 0,
          averageInvoiceValue: 0,
          salesByClient: [],
          salesByProduct: [],
          salesOverTime: [],
          topProducts: [],
          topClients: [],
        };
      }

      const totalSales = invoices.reduce((sum, inv) => sum + (inv.total_ttc || 0), 0);
      const totalInvoices = invoices.length;
      const averageInvoiceValue = totalInvoices > 0 ? totalSales / totalInvoices : 0;

      // Sales by client
      const clientSales = invoices.reduce((acc, inv) => {
        const clientName = inv.client?.name || 'Unknown';
        acc[clientName] = (acc[clientName] || 0) + (inv.total_ttc || 0);
        return acc;
      }, {} as Record<string, number>);

      const salesByClient = Object.entries(clientSales)
        .map(([client_name, total]) => ({ client_name, total }))
        .sort((a, b) => b.total - a.total);

      // Sales by product
      const productSales = invoices.reduce((acc, inv) => {
        inv.invoice_items?.forEach((item: any) => {
          const productName = item.product?.name || 'Unknown';
          if (!acc[productName]) {
            acc[productName] = { quantity: 0, total: 0 };
          }
          acc[productName].quantity += item.quantity || 0;
          acc[productName].total += item.total_ht || 0;
        });
        return acc;
      }, {} as Record<string, { quantity: number; total: number }>);

      const salesByProduct = Object.entries(productSales)
        .map(([product_name, data]) => ({ product_name, ...data }))
        .sort((a, b) => b.total - a.total);

      // Sales over time (daily)
      const dailySales = invoices.reduce((acc, inv) => {
        const date = format(new Date(inv.invoice_date), 'yyyy-MM-dd');
        acc[date] = (acc[date] || 0) + (inv.total_ttc || 0);
        return acc;
      }, {} as Record<string, number>);

      const salesOverTime = Object.entries(dailySales)
        .map(([date, amount]) => ({ date, amount }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Top 10 products
      const topProducts = salesByProduct
        .slice(0, 10)
        .map(p => ({ name: p.product_name, quantity: p.quantity, revenue: p.total }));

      // Top 10 clients
      const topClients = salesByClient
        .slice(0, 10)
        .map(c => ({
          name: c.client_name,
          invoices: invoices.filter(inv => inv.client?.name === c.client_name).length,
          revenue: c.total,
        }));

      return {
        totalSales,
        totalInvoices,
        averageInvoiceValue,
        salesByClient,
        salesByProduct,
        salesOverTime,
        topProducts,
        topClients,
      };
    },
    enabled: !!companyId,
  });
}

// ================================================
// EXPENSE REPORT DATA
// ================================================

export interface ExpenseReportData {
  totalExpenses: number;
  expensesByCategory: { category: string; amount: number; percentage: number }[];
  expensesBySupplier: { supplier_name: string; amount: number }[];
  expensesOverTime: { date: string; amount: number }[];
  monthlyComparison: { month: string; amount: number }[];
}

export function useExpenseReportData(
  companyId: string,
  startDate: Date,
  endDate: Date
) {
  return useQuery({
    queryKey: ['expense-report', companyId, startDate, endDate],
    queryFn: async (): Promise<ExpenseReportData> => {
      const { data: expenses } = await supabase
        .from('treasury_transactions')
        .select('*')
        .eq('company_id', companyId)
        .eq('type', 'expense')
        .gte('transaction_date', startDate.toISOString())
        .lte('transaction_date', endDate.toISOString());

      if (!expenses) {
        return {
          totalExpenses: 0,
          expensesByCategory: [],
          expensesBySupplier: [],
          expensesOverTime: [],
          monthlyComparison: [],
        };
      }

      const totalExpenses = expenses.reduce((sum, exp) => sum + Math.abs(exp.amount || 0), 0);

      // Expenses by category
      const categoryExpenses = expenses.reduce((acc, exp) => {
        const category = exp.category || 'Uncategorized';
        acc[category] = (acc[category] || 0) + Math.abs(exp.amount || 0);
        return acc;
      }, {} as Record<string, number>);

      const expensesByCategory = Object.entries(categoryExpenses)
        .map(([category, amount]) => ({
          category,
          amount,
          percentage: (amount / totalExpenses) * 100,
        }))
        .sort((a, b) => b.amount - a.amount);

      // Expenses by supplier (if linked)
      const supplierExpenses = expenses.reduce((acc, exp) => {
        if (exp.client_id) {
          // TODO: Join with suppliers table
          acc['Supplier'] = (acc['Supplier'] || 0) + Math.abs(exp.amount || 0);
        }
        return acc;
      }, {} as Record<string, number>);

      const expensesBySupplier = Object.entries(supplierExpenses)
        .map(([supplier_name, amount]) => ({ supplier_name, amount }))
        .sort((a, b) => b.amount - a.amount);

      // Expenses over time
      const dailyExpenses = expenses.reduce((acc, exp) => {
        const date = format(new Date(exp.transaction_date), 'yyyy-MM-dd');
        acc[date] = (acc[date] || 0) + Math.abs(exp.amount || 0);
        return acc;
      }, {} as Record<string, number>);

      const expensesOverTime = Object.entries(dailyExpenses)
        .map(([date, amount]) => ({ date, amount }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Monthly comparison (last 6 months)
      const monthlyExpenses = expenses.reduce((acc, exp) => {
        const month = format(new Date(exp.transaction_date), 'MMM yyyy');
        acc[month] = (acc[month] || 0) + Math.abs(exp.amount || 0);
        return acc;
      }, {} as Record<string, number>);

      const monthlyComparison = Object.entries(monthlyExpenses)
        .map(([month, amount]) => ({ month, amount }))
        .sort((a, b) => a.month.localeCompare(b.month));

      return {
        totalExpenses,
        expensesByCategory,
        expensesBySupplier,
        expensesOverTime,
        monthlyComparison,
      };
    },
    enabled: !!companyId,
  });
}

// ================================================
// CASH FLOW REPORT DATA
// ================================================

export interface CashFlowReportData {
  currentBalance: number;
  totalIncome: number;
  totalExpenses: number;
  netCashFlow: number;
  cashFlowOverTime: { date: string; income: number; expenses: number; balance: number }[];
  forecast7Days: number;
  forecast30Days: number;
  forecast90Days: number;
}

export function useCashFlowReportData(
  companyId: string,
  startDate: Date,
  endDate: Date
) {
  return useQuery({
    queryKey: ['cashflow-report', companyId, startDate, endDate],
    queryFn: async (): Promise<CashFlowReportData> => {
      const { data: transactions } = await supabase
        .from('treasury_transactions')
        .select('*')
        .eq('company_id', companyId)
        .gte('transaction_date', startDate.toISOString())
        .lte('transaction_date', endDate.toISOString())
        .order('transaction_date');

      if (!transactions) {
        return {
          currentBalance: 0,
          totalIncome: 0,
          totalExpenses: 0,
          netCashFlow: 0,
          cashFlowOverTime: [],
          forecast7Days: 0,
          forecast30Days: 0,
          forecast90Days: 0,
        };
      }

      const totalIncome = transactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + (t.amount || 0), 0);

      const totalExpenses = transactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

      const netCashFlow = totalIncome - totalExpenses;

      // Get current balance from latest transaction
      const { data: latestBalance } = await supabase
        .from('treasury_transactions')
        .select('balance_after')
        .eq('company_id', companyId)
        .order('transaction_date', { ascending: false })
        .limit(1)
        .single();

      const currentBalance = latestBalance?.balance_after || 0;

      // Cash flow over time
      const cashFlowOverTime = transactions.reduce((acc, t) => {
        const date = format(new Date(t.transaction_date), 'yyyy-MM-dd');
        const existing = acc.find(item => item.date === date);
        
        if (existing) {
          if (t.type === 'income') {
            existing.income += t.amount || 0;
          } else {
            existing.expenses += Math.abs(t.amount || 0);
          }
          existing.balance = existing.income - existing.expenses;
        } else {
          acc.push({
            date,
            income: t.type === 'income' ? (t.amount || 0) : 0,
            expenses: t.type === 'expense' ? Math.abs(t.amount || 0) : 0,
            balance: t.type === 'income' ? (t.amount || 0) : -Math.abs(t.amount || 0),
          });
        }
        
        return acc;
      }, [] as { date: string; income: number; expenses: number; balance: number }[]);

      // Simple forecast based on average daily cash flow
      const avgDailyCashFlow = netCashFlow / Math.max(1, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      return {
        currentBalance,
        totalIncome,
        totalExpenses,
        netCashFlow,
        cashFlowOverTime,
        forecast7Days: currentBalance + (avgDailyCashFlow * 7),
        forecast30Days: currentBalance + (avgDailyCashFlow * 30),
        forecast90Days: currentBalance + (avgDailyCashFlow * 90),
      };
    },
    enabled: !!companyId,
  });
}
